import { useDiscardChangesModal } from "@circle-react/components/QuickPostV3/DiscardChangesModal";
import { useHasOpenChildEditorModal } from "@circle-react/components/QuickPostV3/hooks/useChildEditorModalState";
import { useDeferredCompassModalClose } from "@circle-react/components/QuickPostV3/hooks/useDeferredCompassModalClose";
import { useQuickPostV3ModalStore } from "@circle-react/components/QuickPostV3/store";
import type { ModalRootProps } from "@circleco/compass/components/Modal";
import { useCallback } from "react";

type OpenChangeHandler = NonNullable<ModalRootProps["onOpenChange"]>;

interface UseModalCloseOptions {
  onDiscardChanges?: () => void;
}

interface UseModalCloseResult {
  closeModal: () => void;
  forceClose: () => void;
  handleOpenChange: OpenChangeHandler;
  isOpen: boolean;
}

export const useModalClose = ({
  onDiscardChanges,
}: UseModalCloseOptions = {}): UseModalCloseResult => {
  const { isOpen, hide: forceClose } = useDeferredCompassModalClose();
  const discardChangesModal = useDiscardChangesModal();
  const hasOpenChildEditorModal = useHasOpenChildEditorModal();

  const closeModal = useCallback(() => {
    if (useQuickPostV3ModalStore.getState().isDirty) {
      void discardChangesModal.show({
        onDiscardChanges,
        onConfirm: forceClose,
      });
      return;
    }
    forceClose();
  }, [discardChangesModal, forceClose, onDiscardChanges]);

  const handleOpenChange = useCallback<OpenChangeHandler>(
    open => {
      if (open) return;
      if (hasOpenChildEditorModal) return;
      closeModal();
    },
    [closeModal, hasOpenChildEditorModal],
  );

  return {
    closeModal,
    forceClose,
    handleOpenChange,
    isOpen,
  };
};
